import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { loginOwner, createDemoWedding } from '../services/apiService'; // API fonksiyonunuzu import ettiğinizi varsayıyorum
import { Heart, Mail, CheckCircle2, Loader2, X } from 'lucide-react';
import ReCAPTCHA from "react-google-recaptcha";

// URL path'inden düğün kodunu çıkaran yardımcı fonksiyon
function extractCodeFromPath(path = '') {
  const m = String(path || '').match(/\/w\/([^/?#]+)/);
  return m?.[1] || '';
}

export default function OwnerLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  // Demo Modal State
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [demoSuccess, setDemoSuccess] = useState(false);
  const [demoData, setDemoData] = useState(null);
  const [demoError, setDemoError] = useState('');

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get('next');

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const recaptchaRef = useRef();
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const handleOpenDemoModal = () => {
    setClientEmail('');
    setRecaptchaToken(null);
    setDemoSuccess(false);
    setDemoData(null);
    setDemoError('');
    setIsDemoModalOpen(true);
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.reset();
      } catch (e) {}
    }
  };

  const handleStartDemo = async (e) => {
    if (e) e.preventDefault();
    setDemoError('');

    // E-posta kontrolü
    if (!clientEmail || !clientEmail.includes('@') || !clientEmail.includes('.')) {
      setDemoError('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }

    // reCAPTCHA kontrolü (Eğer site key tanımlıysa)
    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setDemoError('Lütfen güvenlik doğrulamasını (reCAPTCHA) tamamlayın.');
      return;
    }

    setDemoLoading(true);
    try {
      const res = await createDemoWedding(clientEmail.trim(), recaptchaToken);
      if (res && res.success) {
        setDemoData({
          loginEmail: res.loginEmail,
          password: res.password,
          guestUrl: res.guestUrl,
          code: res.code
        });
        setDemoSuccess(true);
      } else {
        throw new Error(res?.message || 'Demo düğünü oluşturulamadı.');
      }
    } catch (e) {
      setDemoError(e.message || 'Demo başlatılırken bir hata oluştu.');
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.reset();
        } catch (err) {}
      }
      setRecaptchaToken(null);
    } finally {
      setDemoLoading(false);
    }
  };

  useEffect(() => {
    if (params.get('action') === 'demo' || params.get('demo') === 'true') {
      handleOpenDemoModal();
    }
  }, [params]);


  // OwnerLogin.js içindeki handleSubmit fonksiyonunun DOĞRU HALİ

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // ✅ GÜNCELLEME 1: Cevaptan artık 'token' beklemiyoruz.
      // Sadece 'user' ve 'primaryCode' alanlarını alıyoruz.
      const { user, primaryCode } = await loginOwner(email.trim(), password);

      // ✅ GÜNCELLEME 2: Başarı kontrolünü 'user' nesnesinin varlığıyla yapıyoruz.
      if (!user || !user.id) {
        throw new Error('Giriş başarısız oldu. Lütfen bilgilerinizi kontrol edin.');
      }

      // ✅ GÜNCELLEME 3: Token'ı localStorage'a kaydetme satırını SİLİYORUZ.
      // Oturum artık HttpOnly cookie ile otomatik yönetiliyor.
      localStorage.setItem('ownerPrimaryCode', primaryCode || '');

      if (user.role === 'organization') {
        window.location.href = '/organization/dashboard';
        return;
      }

      const codeFromNext = extractCodeFromPath(nextUrl);
      const finalCode = codeFromNext || primaryCode;

      const target = finalCode ? `/owner/w/${finalCode}` : '/owner/dashboard';

      // Tam bir sayfa yenilemesi ile yönlendirme, AuthContext'in güncel durumu yakalamasını sağlar.
      window.location.href = target;

    } catch (e) {
      // Backend'den gelen 401 gibi hatalar da bu bloğa düşer.
      setError(e.message || 'Bilinmeyen bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const logoSrc = (process.env.PUBLIC_URL || "") + "/logo.png"; // public/logo.png

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-rose-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <span className="size-12 md:size-16 rounded-2xl bg-rose-500/10 ring-1 ring-rose-200/50 relative overflow-hidden grid place-items-center">
              <img
                src={logoSrc}
                alt="Anora"
                className="w-[44px] h-[44px] md:w-[60px] md:h-[60px] object-contain"
                aria-hidden
              />
            </span>
            <span className="font-semibold tracking-tight text-3xl text-gray-800">
              <span className="text-[#e45078] font-cormorant font-bold text-4xl">Anora</span>
            </span>
          </Link>
          <h1 className="mt-4 text-2xl font-bold text-gray-700">Sahip Girişi</h1>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg border border-rose-100">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">E-posta</label>
              <input
                id="email"
                type="email"
                className="mt-1 w-full p-3 border border-gray-300 rounded-md focus:ring-rose-500 focus:border-rose-500"
                placeholder="ornek@mail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Şifre</label>
              <input
                id="password"
                type="password"
                className="mt-1 w-full p-3 border border-gray-300 rounded-md focus:ring-rose-500 focus:border-rose-500"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && <div className="text-sm text-red-600 bg-red-100 p-3 rounded-md">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#e45078] text-white font-bold py-3 px-4 rounded-md hover:bg-rose-600 disabled:bg-rose-300 transition-colors"
            >
              {loading ? 'Giriş yapılıyor…' : 'Giriş Yap'}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">Veya üyeliksiz dene</span>
            </div>
          </div>

          <button
            onClick={handleOpenDemoModal}
            disabled={loading}
            type="button"
            className="w-full bg-gradient-to-r from-purple-600 to-rose-500 hover:from-purple-700 hover:to-rose-600 text-white font-bold py-3 px-4 rounded-md transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
          >
            🚀 Hızlı Demo Başlat
          </button>

          {/* ✅ YENİ EKLENDİ: Şifremi Unuttum / Şifremi Oluştur Linki */}
          <div className="text-center mt-6">
            <Link
              to="/sifremi-unuttum"
              className="text-sm font-medium text-rose-600 hover:text-rose-500 hover:underline"
            >
              Şifremi Unuttum / Şifremi Oluştur
            </Link>
          </div>
        </div>
      </div>

      {/* 🚀 HIZLI DEMO MODAL */}
      {isDemoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-rose-100 p-6 sm:p-8 animate-in zoom-in-95 duration-200">
            {/* Kapat Butonu */}
            <button
              onClick={() => setIsDemoModalOpen(false)}
              className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              aria-label="Kapat"
            >
              <X className="w-5 h-5" />
            </button>

            {!demoSuccess ? (
              <form onSubmit={handleStartDemo} className="space-y-5">
                <div className="text-center">
                  <div className="mx-auto size-12 rounded-full bg-rose-100 flex items-center justify-center text-[#e45078] mb-3">
                    <Heart className="w-6 h-6 fill-current animate-pulse" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Ücretsiz Demo Başlat</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Anora düğün galerisi özelliklerini hemen deneyimleyin. Demo giriş bilgileriniz ve karekodunuz anında burada gösterilecektir.
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="demo-email" className="block text-sm font-semibold text-gray-700">
                    E-posta Adresiniz
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                      <Mail className="w-5 h-5" />
                    </div>
                    <input
                      id="demo-email"
                      type="email"
                      required
                      placeholder="adiniz@ornek.com"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-400 focus:border-rose-400 transition-all text-gray-800 placeholder-gray-400 text-sm"
                    />
                  </div>
                </div>

                {/* reCAPTCHA widget'ı (Sadece Demo için) */}
                {RECAPTCHA_SITE_KEY && (
                  <div className="flex justify-center py-1">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={(token) => setRecaptchaToken(token)}
                      onExpired={() => setRecaptchaToken(null)}
                    />
                  </div>
                )}

                {demoError && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 p-3 rounded-xl font-medium">
                    {demoError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsDemoModalOpen(false)}
                    className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
                  >
                    Vazgeç
                  </button>
                  <button
                    type="submit"
                    disabled={demoLoading}
                    className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-rose-500 hover:from-purple-700 hover:to-rose-600 text-white font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-75 disabled:cursor-not-allowed"
                  >
                    {demoLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Hazırlanıyor...
                      </>
                    ) : (
                      'Demo Oluştur 🚀'
                    )}
                  </button>
                </div>
              </form>
            ) : (
              demoData && (
                <div className="text-center space-y-4 py-1">
                  <div className="mx-auto size-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-500 mb-1">
                    <CheckCircle2 className="w-8 h-8 animate-bounce" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Demo Hesabınız Hazır! 🎉</h3>
                  
                  {/* Karekod Görseli */}
                  <div className="bg-gray-50 border border-gray-100 rounded-2xl p-3 flex flex-col items-center justify-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(demoData.guestUrl)}`}
                      alt="Demo QR Code"
                      className="border border-gray-200 p-2 rounded-xl bg-white shadow-sm"
                      style={{ width: '150px', height: '150px' }}
                    />
                    <span className="text-[10px] font-bold text-rose-500 mt-2 tracking-wide uppercase">Misafir Giriş QR Kodu</span>
                  </div>

                  {/* Giriş Bilgileri */}
                  <div className="bg-rose-50 border border-rose-100 rounded-xl p-3.5 text-left space-y-2">
                    <div>
                      <span className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">E-posta Adresi</span>
                      <span className="text-xs font-bold text-gray-800 break-all select-all">{demoData.loginEmail}</span>
                    </div>
                    <div>
                      <span className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Geçici Şifre</span>
                      <span className="text-xs font-bold text-rose-600 tracking-wide select-all">{demoData.password}</span>
                    </div>
                  </div>

                  {/* Açıklama Metni */}
                  <p className="text-xs text-gray-600 leading-relaxed text-left bg-gray-50 border border-gray-100 rounded-xl p-3.5">
                    ℹ️ Telefonunuzdan aşağıdaki QR kodu taratarak misafir olarak fotoğraf, video yükleyebilir veya anı defteri yazabilirsiniz. Daha sonrasında giriş bilgileriniz ile giriş yaparak sistemi test edebilirsiniz. <strong>Deneme yapmadan önce QR ve şifrenizi unutmamanız açısından bu ekranın görüntüsünü almanızı tavsiye ederiz.</strong>
                  </p>

                  <button
                    onClick={() => {
                      setEmail(demoData.loginEmail);
                      setPassword(demoData.password);
                      setIsDemoModalOpen(false);
                      localStorage.setItem('ownerPrimaryCode', demoData.code);
                    }}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold text-sm shadow-md transition-colors flex items-center justify-center gap-2"
                  >
                    🔑 Bilgileri Doldur ve Giriş Yap
                  </button>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}